import { createContext, useMemo, useState, type ReactNode } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

/**
 * `_app` module **84453** — the cart drawer's open state, ported verbatim.
 *
 * ```js
 * let s = createContext({ open: !1, setOpen: () => null, isTemporary: !1 });
 * function l({ children }) {
 *   let [r, l] = useState(!1),
 *       u = useMediaQuery("down", o.nk);          // nk === "lg" (900)
 *   return <s.Provider value={{ open: r, setOpen: l, isTemporary: u }}>{children}</s.Provider>;
 * }
 * ```
 *
 * It is mounted in the original's `_app` provider stack (`sl.Z`, immediately
 * inside the offers provider), not on the menu page — because four separate
 * consumers read it: the order bar (`q`), the cart drawer itself (`eK`), the
 * offers drawer, and `orders/[id]`.
 *
 * ⚠ `isTemporary` is ONLY the sub-`lg` flag. The cart drawer's own variant is
 * `isTemporary || isModernDesign ? "temporary" : "permanent"` — the design-type
 * half is applied by the consumer, not stored here.
 */
export interface CartDrawerContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** `useMediaQuery(down("lg"))` — the drawer is an overlay below 900px. */
  isTemporary: boolean;
}

export const CartDrawerContext = createContext<CartDrawerContextValue>({
  open: false,
  setOpen: () => null,
  isTemporary: false,
});

export function CartDrawerProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const isTemporary = useMediaQuery(theme.breakpoints.down("lg"));

  const value = useMemo<CartDrawerContextValue>(
    () => ({ open, setOpen, isTemporary }),
    [open, isTemporary],
  );

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  );
}
